
import { Card, CardContent } from '@/components/ui/card';
import { useTestimonials } from '@/hooks/useTestimonials';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import type { CarouselApi } from "@/components/ui/carousel";

const Testimonials = () => {
  const { data: testimonials, isLoading } = useTestimonials();
  const [api, setApi] = useState<CarouselApi>();
  const [isHovered, setIsHovered] = useState(false);
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    if (!api || !testimonials || testimonials.length <= 3 || isHovered) {
      return;
    }

    const intervalId = setInterval(() => {
      api.scrollNext();
    }, 4000); // Auto-scroll every 4 seconds

    return () => clearInterval(intervalId);
  }, [api, testimonials, isHovered]);

  if (isLoading) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold">What Our Customers Say</h2>
            <p className="mt-4 text-lg text-muted-foreground">We're proud to have happy plant parents.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-8 flex flex-col items-center text-center">
                  <Skeleton className="w-20 h-20 rounded-full mb-4" />
                  <Skeleton className="h-4 w-full mt-2" />
                  <Skeleton className="h-4 w-3/4 mt-2" />
                  <Skeleton className="h-4 w-1/2 mt-4" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  // Duplicate testimonials for smooth infinite loop if there are fewer than 6
  const displayTestimonials = testimonials.length < 6 
    ? [...testimonials, ...testimonials] 
    : testimonials;

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold">What Our Customers Say</h2>
          <p className="mt-4 text-lg text-muted-foreground">We're proud to have happy plant parents.</p>
        </div>
        <div className="mt-12">
          <Carousel
            setApi={setApi}
            opts={{
              loop: true,
              watchDrag: true,
            }}
            className="w-full max-w-md sm:max-w-2xl md:max-w-4xl lg:max-w-6xl mx-auto group touch-pan-y"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <CarouselContent className="-ml-4">
              {displayTestimonials.map((testimonial, index) => (
                <CarouselItem key={`${testimonial.id}-${index}`} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                  <div className="h-[340px] w-full max-w-[380px] mx-auto">
                    <Card className="h-full w-full flex flex-col">
                      <CardContent className="p-8 flex flex-col items-center text-center h-full">
                        <img 
                          src={testimonial.avatar_url || `https://ui-avatars.com/api/?name=${testimonial.name.replace(/\s/g, '+')}&background=random`} 
                          alt={testimonial.name} 
                          className="w-20 h-20 rounded-full mb-4 object-cover flex-shrink-0" 
                        />
                        <p className="text-muted-foreground italic flex-grow min-h-[80px] flex items-center">"{testimonial.quote}"</p>
                        <p className="mt-4 font-semibold text-foreground flex-shrink-0">- {testimonial.name}</p>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CarouselNext className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Carousel>
          
          {testimonials && testimonials.length > 1 && (
            <div className="flex justify-center items-center gap-0 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className="p-2 flex items-center justify-center"
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  <span className={`block rounded-full transition-all duration-300 ${
                    (current % testimonials.length) === index 
                      ? 'w-8 h-2 bg-primary' 
                      : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
